import type { Surah } from '@/types/domain';
import { revelationLabel } from '@/lib/utils/format';

type SurahHeaderProps = {
  surah: Pick<
    Surah,
    'id' | 'name_ar' | 'name_en' | 'name_translation' | 'revelation_type' | 'ayah_count'
  >;
};

/**
 * Page hero for /surah/:id.
 *
 * SEO note: there is exactly one <h1> per page (this one). The H1
 * wraps BOTH the Arabic calligraphy and the English heading so
 * Googlebot reads "Surah Al-Fatihah — The Opening" as the primary
 * heading instead of getting only the Arabic glyphs. Visual hierarchy
 * is preserved by sizing the Arabic span as the larger display element.
 *
 * The `lang` attributes (`ar`, `en`) tell screen readers and the
 * search index which language each span is in — important for the
 * Arabic span which would otherwise be announced as English text.
 */
export function SurahHeader({ surah }: SurahHeaderProps) {
  return (
    <header className="text-center pt-12 pb-10">
      <div className="flex items-center justify-center gap-2 mb-5">
        <span className="pill">Surah {surah.id}</span>
        <span className="pill">{revelationLabel(surah.revelation_type)}</span>
        <span className="pill">{surah.ayah_count} ayahs</span>
      </div>

      <h1>
        <span
          dir="rtl"
          lang="ar"
          className="block font-arabic text-5xl sm:text-6xl text-ink-900 dark:text-ink-50 mb-3"
        >
          {surah.name_ar}
        </span>
        <span
          lang="en"
          className="block font-serif text-2xl sm:text-3xl text-ink-800 dark:text-ink-100"
        >
          Surah {surah.name_en}
          {surah.name_translation && (
            <span className="ml-2 italic text-ink-500 dark:text-ink-400">
              — {surah.name_translation}
            </span>
          )}
        </span>
      </h1>

      <div className="mx-auto mt-10 h-px w-24 bg-accent/40" />
    </header>
  );
}
